import { io, type Socket } from "socket.io-client";
import { getAccessToken, refreshAccessToken } from "./api.ts";
import { apiOrigin } from "./native.ts";

let socket: Socket | null = null;

/** Sunucu saati ile istemci saati arasındaki fark (ms). Geri sayım bununla düzeltilir. */
let clockOffset = 0;

export function serverNow(): number {
  return Date.now() + clockOffset;
}

async function measureClockOffset(active: Socket): Promise<void> {
  // Gidiş-dönüş süresinin yarısını düşerek sunucu saatini tahmin ediyoruz.
  const sentAt = Date.now();
  const serverTime = await active
    .timeout(4000)
    .emitWithAck("time:sync", sentAt)
    .catch(() => null);
  if (typeof serverTime !== "number") return;
  const receivedAt = Date.now();
  clockOffset = serverTime + (receivedAt - sentAt) / 2 - receivedAt;
}

const DEVICE_ID_KEY = "mk-device-id";
let memoryDeviceId: string | null = null;

/**
 * Bu tarayıcıya özgü rastgele kimlik. Sunucu rastgele eşleşmede aynı cihazdaki
 * iki hesabı birbirine düşürmemek için kullanıyor; kimliği tanımlamaz.
 * `getRandomValues` yerel ağdaki http adresinde de çalışıyor, `randomUUID` çalışmıyor.
 */
function deviceId(): string {
  try {
    const stored = window.localStorage.getItem(DEVICE_ID_KEY);
    if (stored && /^[a-f0-9]{32}$/.test(stored)) return stored;
  } catch { /* Depolama kapalıysa bellekte tut. */ }
  if (!memoryDeviceId) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    memoryDeviceId = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  try { window.localStorage.setItem(DEVICE_ID_KEY, memoryDeviceId); } catch { /* Bellekte devam. */ }
  return memoryDeviceId;
}

export function getSocket(): Socket {
  if (socket) return socket;

  const active = io(apiOrigin(), {
    // Her (yeniden) bağlanışta jeton yeniden okunur.
    auth: (cb) => cb({ token: getAccessToken(), deviceId: deviceId() }),
    transports: ["websocket", "polling"],
    reconnectionDelay: 500,
    reconnectionDelayMax: 4000,
  });

  active.on("connect", () => {
    void measureClockOffset(active);
  });

  /*
   * Erişim jetonu 15 dakikada bir düşüyor. Sekme uzun süre arka planda kalıp
   * yeniden bağlanmaya çalıştığında el sıkışma reddedilir; jetonu tazeleyip
   * tekrar deniyoruz, yoksa oyuncu sessizce çevrimdışı kalırdı.
   */
  active.on("connect_error", (error) => {
    if (error.message !== "auth_required") return;
    void refreshAccessToken().then((fresh) => {
      if (fresh && socket === active) active.connect();
    });
  });

  socket = active;
  return active;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
  clockOffset = 0;
}

/** Rehandshake with the current access token while preserving event listeners. */
export function reconnectSocket(): void {
  if (!socket) return;
  socket.disconnect();
  socket.connect();
}
